import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  getEmpleadosHorarios, 
  getSedesHorarios, 
  getCargosHorarios, 
  asignarCodigoEmpleado,
  autoAsignarCodigosSysplus
} from '../services/horariosApi';

export default function DirectorioEmpleados({ usuario, onVolver }) {
  const [empleados, setEmpleados] = useState([]);
  const [sedes, setSedes] = useState([]);
  const [cargos, setCargos] = useState([]);
  
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('ACTIVOS');
  const [filtroSede, setFiltroSede] = useState('');
  const [notificacion, setNotificacion] = useState({ texto: '', tipo: '' });

  const [modalVisible, setModalVisible] = useState(false);
  const [modoAsistenteRapido, setModoAsistenteRapido] = useState(false);
  const [empActivo, setEmpActivo] = useState(null);
  const [formAsignacion, setFormAsignacion] = useState({ sede_id: '', cargo_id: '' });
  const [procesando, setProcesando] = useState(false);
  
  const [procesandoMasivo, setProcesandoMasivo] = useState(false);

  const mostrarMensaje = useCallback((texto, tipo = 'success') => {
    setNotificacion({ texto, tipo });
    setTimeout(() => setNotificacion({ texto: '', tipo: '' }), 4500);
  }, []);

  const cargarDatosBase = useCallback(async () => {
    setCargando(true);
    try {
      const [resEmp, resSedes, resCargos] = await Promise.all([
        getEmpleadosHorarios(),
        getSedesHorarios(),
        getCargosHorarios()
      ]);
      if (resEmp && resEmp.success) setEmpleados(resEmp.data || []);
      if (resSedes && resSedes.success) setSedes(resSedes.data || []);
      if (resCargos && resCargos.success) setCargos(resCargos.data || []);
    } catch (err) {
      mostrarMensaje("Error cargando los datos del directorio: " + err.message, "error");
    } finally {
      setCargando(false);
    }
  }, [mostrarMensaje]);

  useEffect(() => { cargarDatosBase(); }, [cargarDatosBase]);

  const esEmpleadoActivo = (emp) => {
    const est = String((emp && (emp.estado || emp.empl_estado)) || 'ACTIVO').toUpperCase().trim();
    return est === 'ACTIVO' || est === 'A' || est === '1';
  };

  const deducirSedeYCargoSugeridos = (empleado) => {
    let sedeSugerida = empleado.sede_id || '';
    let cargoSugerido = empleado.cargo_id || '';

    const textoSedeSys = String(empleado.sede_original || '').toUpperCase().trim();
    const textoCargoSys = String(empleado.cargo_original || '').toUpperCase().trim();

    if (!sedeSugerida && textoSedeSys && sedes.length > 0) {
      let matchSede = sedes.find(s => String(s.nombre || '').toUpperCase().trim() === textoSedeSys);
      if (!matchSede) {
        const sedesOrdenadas = [...sedes].sort((a, b) => String(b.nombre).length - String(a.nombre).length);
        matchSede = sedesOrdenadas.find(s => textoSedeSys.includes(String(s.nombre).toUpperCase().trim()));
      }
      if (matchSede) sedeSugerida = matchSede.id;
    }

    if (!cargoSugerido && textoCargoSys && cargos.length > 0) {
      let matchCargo = cargos.find(c => String(c.nombre || '').toUpperCase().trim() === textoCargoSys);
      
      if (!matchCargo) {
        const cargosOrdenados = [...cargos].sort((a, b) => String(b.nombre).length - String(a.nombre).length);
        matchCargo = cargosOrdenados.find(c => {
          const n = String(c.nombre).toUpperCase().trim();
          return textoCargoSys.includes(n);
        });
      }

      if (!matchCargo) {
        if (textoCargoSys.includes('CAJERO')) matchCargo = cargos.find(c => String(c.nombre).toUpperCase().includes('CAJERO'));
        else if (textoCargoSys.includes('SURTIDOR')) matchCargo = cargos.find(c => String(c.nombre).toUpperCase().includes('SURTIDOR'));
        else if (textoCargoSys.includes('FRUVER')) matchCargo = cargos.find(c => String(c.nombre).toUpperCase().includes('FRUVER'));
        else if (textoCargoSys.includes('PERDIDA')) matchCargo = cargos.find(c => String(c.nombre).toUpperCase().includes('PERDIDA'));
        else if (textoCargoSys.includes('RECIBO')) matchCargo = cargos.find(c => String(c.nombre).toUpperCase().includes('RECIBO'));
      }
      if (matchCargo) cargoSugerido = matchCargo.id;
    }

    return { sede_id: sedeSugerida, cargo_id: cargoSugerido };
  };

  const listaPendientes = useMemo(() => {
    return empleados.filter(e => esEmpleadoActivo(e) && !e.codigo_interno);
  }, [empleados]);

  const abrirModalManual = (empleado) => {
    if (!esEmpleadoActivo(empleado)) return mostrarMensaje("⛔ No se puede asignar código a personal RETIRADO.", "error");
    setModoAsistenteRapido(false);
    setEmpActivo(empleado);
    setFormAsignacion(deducirSedeYCargoSugeridos(empleado));
    setModalVisible(true);
  };

  const iniciarAsistenteSemiManual = () => {
    if (listaPendientes.length === 0) return mostrarMensaje(" Todos los colaboradores activos ya tienen su código asignado.", "success");
    const primero = listaPendientes[0];
    setModoAsistenteRapido(true);
    setEmpActivo(primero);
    setFormAsignacion(deducirSedeYCargoSugeridos(primero));
    setModalVisible(true);
  };

  const saltarAlSiguientePendiente = () => {
    if (!empActivo || listaPendientes.length <= 1) return;
    const idxActual = listaPendientes.findIndex(e => e.id === empActivo.id);
    const siguiente = listaPendientes[(idxActual + 1) % listaPendientes.length];
    setEmpActivo(siguiente);
    setFormAsignacion(deducirSedeYCargoSugeridos(siguiente));
  };

  const handleAutoAsignarMasivo = async () => {
    if (!window.confirm(`¿Estás seguro de auto-asignar códigos a los ${listaPendientes.length} colaboradores pendientes?\n\nEl sistema cruzará la Sede y Cargo de Sysplus directamente con tus tablas maestras.`)) return;
    setProcesandoMasivo(true);
    try {
      const res = await autoAsignarCodigosSysplus();
      mostrarMensaje(res.mensaje || "Asignación masiva completada.", "success");
      await cargarDatosBase(); 
    } catch (error) { mostrarMensaje("Error en la asignación masiva: " + error.message, "error"); } 
    finally { setProcesandoMasivo(false); }
  };

  const infoOcupacionSedeSeleccionada = useMemo(() => {
    if (!formAsignacion.sede_id) return null;
    const sedeObj = sedes.find(s => String(s.id) === String(formAsignacion.sede_id));
    if (!sedeObj) return null;

    const ocupados = empleados.filter(e => 
      esEmpleadoActivo(e) && e.codigo_interno && String(e.sede_id) === String(sedeObj.id) && (!empActivo || e.id !== empActivo.id)
    ).length;

    const limiteCupos = Number(sedeObj.planta_autorizada || 20);

    return {
      nombre: sedeObj.nombre, 
      prefijo: sedeObj.prefijo || 'GEN',
      ocupados, 
      limite: limiteCupos, 
      desglose: sedeObj.detalle_planta || 'Personal operativo estándar',
      lleno: ocupados >= limiteCupos
    };
  }, [formAsignacion.sede_id, sedes, empleados, empActivo]);

  const handleGuardarCodigo = async (e) => {
    e.preventDefault();
    if (!empActivo || !esEmpleadoActivo(empActivo)) return;
    if (!formAsignacion.sede_id || !formAsignacion.cargo_id) return mostrarMensaje("⚠️ Debes seleccionar la Sede y el Cargo.", "error");

    setProcesando(true);
    try {
      // Envía estrictamente las llaves foráneas para mantener integridad relacional
      const res = await asignarCodigoEmpleado(empActivo.id, formAsignacion.sede_id, formAsignacion.cargo_id);
      const sedeObj = sedes.find(s => String(s.id) === String(formAsignacion.sede_id));
      const cargoObj = cargos.find(c => String(c.id) === String(formAsignacion.cargo_id));

      const nuevaLista = empleados.map(item => 
        item.id === empActivo.id 
          ? { ...item, codigo_interno: res.codigo, sede_id: formAsignacion.sede_id, sede_nombre: sedeObj ? sedeObj.nombre : item.sede_nombre, cargo_id: formAsignacion.cargo_id, cargo_nombre: cargoObj ? cargoObj.nombre : item.cargo_nombre } 
          : item
      );
      setEmpleados(nuevaLista);
      mostrarMensaje(`✅ Código ${res.codigo} asignado a ${empActivo.nombre_completo}`, "success");

      if (modoAsistenteRapido) {
        const restantes = nuevaLista.filter(x => esEmpleadoActivo(x) && !x.codigo_interno);
        if (restantes.length > 0) {
          const proximo = restantes[0];
          setEmpActivo(proximo); setFormAsignacion(deducirSedeYCargoSugeridos(proximo));
        } else {
          setModalVisible(false); mostrarMensaje("¡Excelente! Has terminado de asignar todos los pendientes.", "success");
        }
      } else {
        setModalVisible(false);
      }
    } catch (error) { mostrarMensaje("Error al asignar código: " + error.message, "error"); } 
    finally { setProcesando(false); }
  };

  const empleadosFiltrados = empleados.filter((emp) => {
    const activo = esEmpleadoActivo(emp);
    if (filtroEstado === 'ACTIVOS' && !activo) return false;
    if (filtroEstado === 'RETIRADOS' && activo) return false;
    if (filtroEstado === 'CON_CODIGO' && (!activo || !emp.codigo_interno)) return false;
    if (filtroEstado === 'PENDIENTES' && (!activo || emp.codigo_interno)) return false;

    if (filtroSede) {
      const matchId = String(emp.sede_id) === String(filtroSede);
      const matchNombre = String(emp.sede_nombre || emp.sede_original || '').toLowerCase().includes(filtroSede.toLowerCase());
      if (!matchId && !matchNombre) return false;
    }

    const texto = `${emp.nombre_completo || ''} ${emp.documento || ''} ${emp.codigo_interno || ''} ${emp.sede_original || ''} ${emp.cargo_original || ''}`.toLowerCase();
    return texto.includes(busqueda.toLowerCase());
  });

  const totalActivos = empleados.filter(esEmpleadoActivo).length;
  const totalConCodigo = empleados.filter(e => esEmpleadoActivo(e) && e.codigo_interno).length;
  const totalPendientes = listaPendientes.length;

  return (
    <div className="animate-fade-in relative pb-10">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center border-b border-gray-200 pb-4 mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800">👥 Directorio de Personal y Códigos</h2>
          <p className="text-xs text-gray-500 font-bold uppercase tracking-wider mt-1">Sincronización en tiempo real con Sysplus y Auto-Reciclaje de Cupos</p>
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <button onClick={cargarDatosBase} className="px-4 py-2.5 rounded-xl bg-sky-50 hover:bg-sky-100 text-sky-700 font-black text-xs border border-sky-200 shadow-sm transition flex items-center gap-1.5 cursor-pointer">
            🔄 Sincronizar Sysplus
          </button>
          {totalPendientes > 0 && (
            <>
              <button onClick={handleAutoAsignarMasivo} disabled={procesandoMasivo} className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs shadow-md transition flex items-center gap-2 cursor-pointer disabled:opacity-50">
                <span>{procesandoMasivo ? '🪄 Asignando...' : `🪄 Auto-Asignar Todos (${totalPendientes})`}</span>
              </button>
              <button onClick={iniciarAsistenteSemiManual} className="px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-black text-xs shadow-md transition flex items-center gap-2 cursor-pointer">
                <span> Asistente 1 a 1</span>
              </button>
            </>
          )}
          <button onClick={onVolver} className="px-5 py-2.5 rounded-xl bg-gray-100 font-bold text-xs hover:bg-gray-200 transition cursor-pointer border border-gray-200">Volver</button>
        </div>
      </div>

      {notificacion.texto && (
        <div className={`mb-5 p-3.5 rounded-2xl text-xs font-bold flex justify-between items-center shadow-sm ${notificacion.tipo === 'error' ? 'bg-red-50 text-red-800 border border-red-200' : 'bg-emerald-50 text-emerald-800 border border-emerald-200'}`}>
          <span>{notificacion.texto}</span>
          <button onClick={() => setNotificacion({ texto: '', tipo: '' })} className="font-black ml-4 cursor-pointer">✕</button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white/80 p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
          <div><p className="text-[10px] font-black uppercase text-gray-400">Personal Activo Sysplus</p><p className="text-2xl font-black text-slate-800">{totalActivos}</p></div>
          <span className="text-2xl bg-emerald-50 p-3 rounded-xl">✅</span>
        </div>
        <div className="bg-white/80 p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
          <div><p className="text-[10px] font-black uppercase text-blue-500">Con Código de Horario</p><p className="text-2xl font-black text-blue-700">{totalConCodigo}</p></div>
          <span className="text-2xl bg-blue-50 p-3 rounded-xl">🏷️</span>
        </div>
        <div className="bg-white/80 p-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between">
          <div><p className="text-[10px] font-black uppercase text-amber-500">Pendientes por Asignar</p><p className="text-2xl font-black text-amber-600">{totalPendientes}</p></div>
          <span className="text-2xl bg-amber-50 p-3 rounded-xl">⏳</span>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-6 items-center justify-between">
        <div className="flex flex-wrap gap-2 w-full md:w-auto">
          {[{ id: 'ACTIVOS', label: 'Activos' }, { id: 'CON_CODIGO', label: 'Con Código' }, { id: 'PENDIENTES', label: 'Sin Código' }, { id: 'RETIRADOS', label: 'Retirados (Sysplus)' }].map(tab => (
            <button key={tab.id} onClick={() => setFiltroEstado(tab.id)} className={`px-3.5 py-2 rounded-xl text-xs font-black transition cursor-pointer ${filtroEstado === tab.id ? 'bg-slate-900 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}>
              {tab.label}
            </button>
          ))}
        </div>
        <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto">
          <select value={filtroSede} onChange={(e) => setFiltroSede(e.target.value)} className="px-3 py-2 rounded-xl border border-gray-200 bg-white text-xs font-bold text-gray-700 outline-none">
            <option value="">Todas las Sedes...</option>
            {sedes.map(s => <option key={s.id} value={s.nombre}>{s.nombre} ({s.prefijo})</option>)}
          </select>
          <input type="text" placeholder="Buscar cédula o código..." value={busqueda} onChange={(e) => setBusqueda(e.target.value)} className="w-full sm:w-72 px-4 py-2 rounded-xl border border-gray-200 bg-white focus:ring-2 focus:ring-blue-500 outline-none text-xs font-medium" />
        </div>
      </div>

      {cargando ? (
        <p className="text-center text-gray-500 text-sm font-bold animate-pulse py-12">Sincronizando directorio con Sysplus...</p>
      ) : (
        <div className="overflow-x-auto bg-white rounded-2xl shadow-sm border border-gray-100 max-h-[60vh] overflow-y-auto custom-scrollbar">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase font-black sticky top-0 z-10 border-b border-gray-100">
              <tr>
                <th className="p-4">Documento</th>
                <th className="p-4">Colaborador / Ubicación</th>
                <th className="p-4 text-center">Estado Sysplus</th>
                <th className="p-4 text-center">Código Interno (ID Turno)</th>
                <th className="p-4 text-center">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {empleadosFiltrados.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-gray-400 font-bold">No se encontraron colaboradores con este filtro.</td></tr>
              ) : (
                empleadosFiltrados.map((emp) => {
                  const activo = esEmpleadoActivo(emp);
                  return (
                    <tr key={emp.id || emp.documento} className={`hover:bg-slate-50 transition ${!activo ? 'bg-red-50/10 opacity-75' : ''}`}>
                      <td className="p-4 font-mono font-bold text-slate-700">{emp.documento}</td>
                      <td className="p-4">
                        <div className={`font-extrabold ${!activo ? 'text-red-900 line-through decoration-red-300' : 'text-gray-900'}`}>{emp.nombre_completo}</div>
                        <div className="text-[10px] text-gray-500 font-semibold mt-1 flex flex-wrap items-center gap-1.5">
                          <span> Sysplus: {emp.sede_original || 'Sin sede'} | {emp.cargo_original || 'Sin cargo'}</span>
                          {emp.sede_nombre && activo && (
                            <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded font-bold border border-blue-100">Asignado: {emp.sede_nombre} ({emp.cargo_nombre})</span>
                          )}
                          {!activo && (
                            <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded font-bold border border-red-100 flex items-center gap-1">✕ Desvinculado del Punto</span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 text-center">
                        {activo ? <span className="px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 font-black text-[10px]">ACTIVO</span> : <span className="px-2.5 py-1 rounded-full bg-red-100 text-red-700 font-black text-[10px]">RETIRADO</span>}
                      </td>
                      <td className="p-4 text-center">
                        {!activo ? (
                          <span className="px-3 py-1 rounded-lg bg-gray-100 text-gray-500 font-bold text-[10px] border border-gray-200 border-dashed">🗑️ Cupo Liberado</span>
                        ) : emp.codigo_interno ? (
                          <span className="px-3 py-1 rounded-lg bg-blue-50 text-blue-700 font-mono font-black border border-blue-200 shadow-sm">{emp.codigo_interno}</span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-lg bg-amber-50 text-amber-600 font-bold border border-amber-200 text-[10px]">⏳ Pendiente</span>
                        )}
                      </td>
                      <td className="p-4 text-center">
                        {activo ? (
                          <button onClick={() => abrirModalManual(emp)} className={`px-3.5 py-1.5 rounded-xl font-black text-[10px] transition cursor-pointer ${emp.codigo_interno ? 'bg-amber-50 text-amber-800 border border-amber-200 hover:bg-amber-100' : 'bg-slate-900 text-white hover:bg-slate-800 shadow-sm'}`}>
                            {emp.codigo_interno ? '✏️ Trasladar' : '+ Generar Código'}
                          </button>
                        ) : (
                          <span className="text-[10px] font-bold text-red-400 italic">No asignable</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {modalVisible && empActivo && (
        <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-[28px] p-6 sm:p-8 w-full max-w-lg shadow-2xl border border-gray-100">
            <div className="flex justify-between items-start mb-2">
              <div>
                <span className={`text-[10px] font-black uppercase px-2.5 py-1 rounded-full ${modoAsistenteRapido ? 'bg-blue-100 text-blue-800' : empActivo.codigo_interno ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>
                  {modoAsistenteRapido ? ` Asistente Semi-Manual (${listaPendientes.length} pendientes)` : empActivo.codigo_interno ? `✏️ Traslado o Cambio de Cargo (Actual: ${empActivo.codigo_interno})` : '➕ Asignación Manual Individual'}
                </span>
                <h3 className="text-xl font-black text-slate-800 mt-2">{empActivo.nombre_completo}</h3>
                <p className="text-xs text-gray-500 font-medium">CC: <b className="text-slate-700">{empActivo.documento}</b> | Sysplus: <b className="text-blue-600">{empActivo.sede_original || 'N/A'} | {empActivo.cargo_original || 'N/A'}</b></p>
              </div>
              <button onClick={() => setModalVisible(false)} className="w-8 h-8 rounded-full bg-gray-100 hover:bg-red-100 hover:text-red-600 font-bold text-gray-500 cursor-pointer">✕</button>
            </div>

            <form onSubmit={handleGuardarCodigo} className="flex flex-col gap-4 mt-5">
              <div>
                <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">1. Punto de Venta / Sede Oficial</label>
                <select required value={formAsignacion.sede_id} onChange={(e) => setFormAsignacion({...formAsignacion, sede_id: e.target.value})} className="w-full p-3 border border-gray-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/60">
                  <option value="">Selecciona una sede...</option>
                  {sedes.map(s => <option key={s.id} value={s.id}>{s.nombre} (Prefijo: {s.prefijo})</option>)}
                </select>
              </div>

              {infoOcupacionSedeSeleccionada && (
                <div className={`p-3.5 rounded-2xl border text-xs ${infoOcupacionSedeSeleccionada.lleno ? 'bg-amber-50 border-amber-200 text-amber-900' : 'bg-emerald-50/70 border-emerald-200 text-emerald-900'}`}>
                  <div className="flex justify-between items-center font-black mb-1">
                    <span>📊 Planta Autorizada ({infoOcupacionSedeSeleccionada.nombre})</span>
                    <span className="px-2 py-0.5 rounded-full bg-white border text-[10px]">{infoOcupacionSedeSeleccionada.ocupados} / {infoOcupacionSedeSeleccionada.limite} cupos ocupados</span>
                  </div>
                  <p className="text-[10px] opacity-80 leading-relaxed"><b>Referencia:</b> {infoOcupacionSedeSeleccionada.desglose}</p>
                </div>
              )}
              
              <div>
                <label className="block text-[10px] font-black text-gray-500 uppercase mb-1">2. Cargo Operativo en el Punto</label>
                <select required value={formAsignacion.cargo_id} onChange={(e) => setFormAsignacion({...formAsignacion, cargo_id: e.target.value})} className="w-full p-3 border border-gray-200 rounded-xl text-xs font-bold text-slate-700 outline-none focus:ring-2 focus:ring-blue-500 bg-slate-50/60">
                  <option value="">Selecciona un cargo...</option>
                  {cargos.map(c => <option key={c.id} value={c.id}>{c.nombre} (Prefijo: {c.prefijo})</option>)}
                </select>
              </div>

              <div className="bg-blue-50/70 border border-blue-100 rounded-xl p-3 text-[11px] text-blue-800 font-medium">
                {empActivo.codigo_interno ? `🔄 Al guardar, si cambiaste la sede o el cargo, el código anterior (${empActivo.codigo_interno}) quedará libre y se le asignará el nuevo consecutivo disponible.` : `💡 El sistema asignará el siguiente consecutivo libre en la sede.`}
              </div>

              <div className="flex flex-wrap gap-2.5 mt-2">
                <button type="button" onClick={() => setModalVisible(false)} className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-bold text-xs text-gray-600 cursor-pointer transition">Cerrar</button>
                {modoAsistenteRapido && listaPendientes.length > 1 && (
                  <button type="button" onClick={saltarAlSiguientePendiente} className="px-4 py-3 bg-slate-100 hover:bg-slate-200 rounded-xl font-bold text-xs text-slate-700 cursor-pointer transition">Saltar ⏭️</button>
                )}
                <button type="submit" disabled={procesando} className="flex-1 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs disabled:opacity-50 shadow-md cursor-pointer transition">
                  {procesando ? 'Procesando...' : modoAsistenteRapido ? '✅ Asignar Código y Pasar al Siguiente' : empActivo.codigo_interno ? '💾 Actualizar Sede / Cargo' : '✅ Guardar y Generar ID'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}